import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormField } from '@connor-music/shared';
import { adminAuthService } from '@shared/services/adminAuth';
import { KeyRound, AlertCircle, CheckCircle } from 'lucide-react';

export default function AdminChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationErrors: Record<string, string> = {};
  if (newPassword && newPassword.length < 12) {
    validationErrors.newPassword = 'Password must be at least 12 characters.';
  }
  if (confirmPassword && newPassword !== confirmPassword) {
    validationErrors.confirmPassword = 'Passwords do not match.';
  }

  const isValid =
    currentPassword.length > 0 &&
    newPassword.length >= 12 &&
    newPassword === confirmPassword &&
    Object.keys(validationErrors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setError('');
    setIsSubmitting(true);

    try {
      const result = await adminAuthService.changePassword(currentPassword, newPassword);

      if (!result.success) {
        setError(result.error || 'Password change failed.');
        return;
      }

      navigate('/admin-setup/dashboard', { replace: true });
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordRequirements = [
    { met: newPassword.length >= 12, label: 'At least 12 characters' },
    { met: /[A-Z]/.test(newPassword), label: 'One uppercase letter' },
    { met: /[a-z]/.test(newPassword), label: 'One lowercase letter' },
    { met: /[0-9]/.test(newPassword), label: 'One number' },
    { met: /[^A-Za-z0-9]/.test(newPassword), label: 'One special character' },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50 mb-4">
            <KeyRound className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Change Password
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            You must change the default admin password before continuing.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField label="Current Password" required htmlFor="current-password">
              <input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400"
              />
            </FormField>

            <FormField
              label="New Password"
              required
              htmlFor="new-password"
              error={validationErrors.newPassword}
            >
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400"
              />
            </FormField>

            <FormField
              label="Confirm New Password"
              required
              htmlFor="confirm-password"
              error={validationErrors.confirmPassword}
            >
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-primary-400 dark:focus:ring-primary-400"
              />
            </FormField>

            {/* Password requirements */}
            {newPassword.length > 0 && (
              <div className="rounded-md bg-gray-50 dark:bg-gray-700/50 p-3">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
                  Password requirements:
                </p>
                <ul className="space-y-1">
                  {passwordRequirements.map((req) => (
                    <li
                      key={req.label}
                      className={`flex items-center gap-1.5 text-xs ${
                        req.met
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      <CheckCircle className={`h-3.5 w-3.5 ${req.met ? 'opacity-100' : 'opacity-30'}`} />
                      {req.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !isValid}
              className="w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary-500 dark:hover:bg-primary-600 dark:focus:ring-offset-gray-800"
            >
              {isSubmitting ? 'Changing Password...' : 'Change Password & Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
